import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  Text,
  Box,
  useToast,
} from '@chakra-ui/react';

interface AddToHomeScreenProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const AddToHomeScreen: React.FC<AddToHomeScreenProps> = ({ isOpen: externalOpen, onClose: externalClose }) => {
  const disclosure = useDisclosure();
  const isOpen = externalOpen !== undefined ? externalOpen : disclosure.isOpen;
  const onClose = externalClose || disclosure.onClose;
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const toast = useToast();

  useEffect(() => {
    const isIOSDevice =
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // If externally controlled and opened, attempt native install for non-iOS
  useEffect(() => {
    if (externalOpen && !isIOS && deferredPrompt) {
      (async () => {
        try {
          await deferredPrompt.prompt();
          const { outcome } = await deferredPrompt.userChoice;
          if (outcome === 'accepted') {
            toast({
              title: 'Success!',
              description: 'App has been added to your home screen.',
              status: 'success',
              position: 'top',
              duration: 3000,
              isClosable: true,
            });
          }
          setDeferredPrompt(null);
          onClose();
        } catch {
          toast({
            title: 'Error',
            description: 'Failed to install the app. Please try again.',
            status: 'error',
            position: 'top',
            duration: 3000,
            isClosable: true,
          });
          onClose();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen, isIOS, deferredPrompt]);

  const iosSteps = [
    {
      n: 1,
      title: 'Open in Safari',
      body: 'This only works in Safari — not Chrome, Firefox, or in-app browsers. If you\'re in another browser, tap the address bar and open this page in Safari first.',
    },
    {
      n: 2,
      title: 'Tap the Share button',
      body: 'It\'s the square icon with an up arrow, located at the bottom of the screen (or top on iPad). If the toolbar is hidden, scroll up slightly to reveal it.',
    },
    {
      n: 3,
      title: 'Scroll and tap "Add to Home Screen"',
      body: 'In the share sheet, swipe up on the action list and tap "Add to Home Screen". It has a grey square icon with a + sign.',
    },
    {
      n: 4,
      title: 'Confirm by tapping "Add"',
      body: 'You can rename the shortcut if you want. Then tap "Add" in the top right corner. The icon will appear on your home screen.',
    },
    {
      n: 5,
      title: 'Launch from home screen',
      body: 'Tap the new icon to open the app in full-screen mode — it will look and feel like a native app, with no Safari toolbar, and works offline.',
    },
  ];

  const androidSteps = [
    {
      n: 1,
      title: 'Open in Chrome',
      body: 'For the best experience use Chrome. Edge, Samsung Internet, and Brave also work. Firefox Android requires a different flow (see their menu).',
    },
    {
      n: 2,
      title: 'Tap the menu (⋮)',
      body: 'Tap the three-dot menu in the top right corner of Chrome to open the browser menu.',
    },
    {
      n: 3,
      title: 'Tap "Install app" or "Add to Home screen"',
      body: 'Look for "Install app" (Chrome\'s newer label) or "Add to Home screen". If neither appears, make sure you\'re on the site\'s main page and try refreshing.',
    },
    {
      n: 4,
      title: 'Confirm the installation',
      body: 'A dialog will appear. Tap "Install" (or "Add"). The app will be added to your home screen and app drawer.',
    },
    {
      n: 5,
      title: 'Open from your home screen',
      body: 'Tap the new icon to launch the app. It will open in its own window without the browser UI, and will work even when offline.',
    },
  ];

  // Order sections with the user's detected platform first
  const sections: Array<{ label: string; steps: typeof iosSteps }> = isIOS
    ? [
        { label: 'For iPhone / iPad', steps: iosSteps },
        { label: 'For Android / Chrome', steps: androidSteps },
      ]
    : [
        { label: 'For Android / Chrome', steps: androidSteps },
        { label: 'For iPhone / iPad', steps: iosSteps },
      ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
        <ModalOverlay bg="rgba(0,0,0,0.6)" backdropFilter="blur(20px)" sx={{ WebkitBackdropFilter: 'blur(20px)' }} />
        <ModalContent
          bg="rgba(3,6,16,0.72)"
          backdropFilter="blur(40px) saturate(180%)"
          color="text.primary"
          mx={4}
          maxH="85vh"
          borderRadius="24px"
          border="1px solid rgba(255,255,255,0.08)"
          boxShadow="0 16px 56px rgba(0,0,0,0.5), 0 1px 0 inset rgba(255,255,255,0.07)"
          sx={{ WebkitBackdropFilter: 'blur(40px) saturate(180%)' }}
        >
          <ModalHeader
            fontSize="lg" fontWeight="700" color="white"
            borderBottom="1px solid rgba(255,255,255,0.08)"
            pb={4} letterSpacing="-0.02em"
          >
            Add to Home Screen
          </ModalHeader>

          {/* Close × button — plain grey outline, no blue focus */}
          <Box
            as="button"
            position="absolute" top={3} right={3}
            w="40px" h="40px"
            display="flex" alignItems="center" justifyContent="center"
            borderRadius="10px"
            border="1px solid rgba(255,255,255,0.12)"
            bg="transparent" outline="none"
            cursor="pointer"
            _hover={{ borderColor: 'rgba(255,255,255,0.25)' }}
            _focus={{ outline: 'none', boxShadow: 'none' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            transition="border-color 0.15s"
            onClick={onClose}
          >
            <Text fontSize="16px" color="rgba(255,255,255,0.5)" lineHeight={1}>✕</Text>
          </Box>

          <ModalBody py={5}>
            <VStack spacing={6} align="stretch">
              {/* Intro blurb */}
              <Text fontSize="13px" color="rgba(255,255,255,0.6)" lineHeight="1.6">
                Install this app on your device for quick access and offline use — no app store required. Follow the steps for your device below.
              </Text>

              {/* Both platform sections */}
              {sections.map((section) => (
                <Box key={section.label}>
                  <Text mb={3} fontSize="12px" fontWeight="800" letterSpacing="0.08em"
                    textTransform="uppercase" color="white">
                    {section.label}
                  </Text>
                  <VStack spacing={2.5} align="stretch">
                    {section.steps.map((step) => (
                      <HStack
                        key={step.n}
                        spacing={3}
                        p={3}
                        border="1px solid rgba(255,255,255,0.08)"
                        borderRadius="14px"
                        align="flex-start"
                      >
                        <Box
                          w="32px" h="32px" borderRadius="full"
                          bg="rgba(255,255,255,0.08)"
                          border="1px solid rgba(255,255,255,0.15)"
                          display="flex" alignItems="center" justifyContent="center"
                          flexShrink={0}
                          mt="1px"
                        >
                          <Text fontSize="13px" fontWeight="800" color="white">{step.n}</Text>
                        </Box>
                        <VStack align="start" spacing={1} flex={1} minW={0}>
                          <Text fontSize="14px" color="white" fontWeight="700" lineHeight="1.3">
                            {step.title}
                          </Text>
                          <Text fontSize="12px" color="rgba(255,255,255,0.6)" fontWeight="400" lineHeight="1.5">
                            {step.body}
                          </Text>
                        </VStack>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ))}

              {/* Tip footer */}
              <Box
                p={3}
                border="1px dashed rgba(255,255,255,0.12)"
                borderRadius="12px"
                bg="rgba(255,255,255,0.02)"
              >
                <Text fontSize="11px" color="rgba(255,255,255,0.55)" lineHeight="1.5">
                  <Text as="span" fontWeight="700" color="rgba(255,255,255,0.75)">Why install?</Text>
                  {' '}Works offline, loads instantly, appears as an icon on your home screen, and runs in full-screen without browser toolbars. No app store, no downloads, no tracking.
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px solid rgba(255,255,255,0.06)" pt={4}>
            <Box
              as="button"
              px={5} py={2}
              borderRadius="10px"
              border="1px solid rgba(255,255,255,0.12)"
              bg="transparent" outline="none"
              cursor="pointer"
              _hover={{ borderColor: 'rgba(255,255,255,0.25)' }}
              _focus={{ outline: 'none', boxShadow: 'none' }}
              _focusVisible={{ outline: 'none', boxShadow: 'none' }}
              transition="border-color 0.15s"
              onClick={onClose}
            >
              <Text fontSize="14px" fontWeight="600" color="rgba(255,255,255,0.5)">
                Close
              </Text>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddToHomeScreen; 